/**
 * AppsFlyer OneLink Smart Script helpers for web-to-app attribution.
 * Rewrites store CTAs to OneLink URLs that carry Meta/TikTok/Google click IDs.
 */

const SCRIPT_ID = 'appsflyer-onelink-smart-script'
const SCRIPT_SRC = '/scripts/onelink-smart-script-latest.js'

/** OneLink custom link (domain + template ID + path). */
export const IVY_ONELINK_TEMPLATE_URL = 'https://ivydating.onelink.me/BNlS/getapp'

export const FALLBACK_APP_STORE_URL = 'https://apps.apple.com/app/ivy-dating/id6479940268'
export const FALLBACK_PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.ivyapp'

type SmartScriptParamConfig = {
  keys?: string[]
  overrideValues?: Record<string, string>
  defaultValue?: string
  paramKey?: string
}

type GenerateOneLinkResult = {
  clickURL: string
}

declare global {
  interface Window {
    AF_SMART_SCRIPT?: {
      generateOneLinkURL: (args: {
        oneLinkURL: string
        afParameters: {
          mediaSource: SmartScriptParamConfig
          campaign?: SmartScriptParamConfig
          channel?: SmartScriptParamConfig
          ad?: SmartScriptParamConfig
          adSet?: SmartScriptParamConfig
          afSub1?: SmartScriptParamConfig
          afSub2?: SmartScriptParamConfig
          afSub3?: SmartScriptParamConfig
          afSub4?: SmartScriptParamConfig
          afSub5?: SmartScriptParamConfig
          googleClickIdKey?: string
        }
        webReferrer?: string
      }) => GenerateOneLinkResult | null
      version?: string
    }
  }
}

let loadPromise: Promise<void> | null = null

function loadSmartScript(): Promise<void> {
  if (typeof window === 'undefined') return Promise.resolve()
  if (window.AF_SMART_SCRIPT?.generateOneLinkURL) return Promise.resolve()
  if (loadPromise) return loadPromise

  loadPromise = new Promise<void>((resolve, reject) => {
    const existing = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null
    if (existing) {
      if (window.AF_SMART_SCRIPT?.generateOneLinkURL) return resolve()
      existing.addEventListener('load', () => resolve(), { once: true })
      existing.addEventListener('error', () => reject(new Error('OneLink Smart Script failed to load')), {
        once: true,
      })
      return
    }

    const script = document.createElement('script')
    script.id = SCRIPT_ID
    script.src = SCRIPT_SRC
    script.async = true
    script.onload = () => resolve()
    script.onerror = () => reject(new Error('OneLink Smart Script failed to load'))
    document.head.appendChild(script)
  })

  return loadPromise
}

/**
 * Generate an attributed OneLink URL from the current page query string.
 * Returns null when the script is unavailable or cannot build a URL.
 */
export async function generateOneLinkClickURL(): Promise<string | null> {
  try {
    await loadSmartScript()
  } catch {
    return null
  }

  if (!window.AF_SMART_SCRIPT?.generateOneLinkURL) return null

  // mediaSource is required by Smart Script. Keys cover AF `pid`, UTM, and our existing `source` param.
  // defaultValue keeps store CTAs on OneLink for organic/direct traffic so click IDs still attach when present.
  const mediaSource: SmartScriptParamConfig = {
    keys: ['pid', 'utm_source', 'source'],
    defaultValue: 'website',
  }
  const campaign: SmartScriptParamConfig = {
    keys: ['c', 'utm_campaign', 'campaign'],
  }
  const channel: SmartScriptParamConfig = {
    keys: ['af_channel', 'utm_medium'],
  }
  const ad: SmartScriptParamConfig = {
    keys: ['af_ad', 'utm_content', 'ad'],
  }
  const adSet: SmartScriptParamConfig = {
    keys: ['af_adset', 'adset'],
  }

  // Smart Script 2.8.1+ auto-forwards fbclid/gclid; map them to af_sub* for raw-data visibility.
  // ttclid auto-forward needs 2.10+; map explicitly so TikTok still attributes on 2.9.x.
  const afSub2: SmartScriptParamConfig = { keys: ['fbclid'] }
  const afSub3: SmartScriptParamConfig = { keys: ['ttclid'] }

  const result = window.AF_SMART_SCRIPT.generateOneLinkURL({
    oneLinkURL: IVY_ONELINK_TEMPLATE_URL,
    afParameters: {
      mediaSource,
      campaign,
      channel,
      ad,
      adSet,
      afSub2,
      afSub3,
      googleClickIdKey: 'af_sub4',
    },
    webReferrer: 'af_sub5',
  })

  return result?.clickURL ?? null
}

export function buildFallbackAppStoreURL(source: string | null): string {
  const ct = source ? encodeURIComponent(source) : 'unknown'
  return `${FALLBACK_APP_STORE_URL}?pt=127856676&ct=${ct}&mt=8`
}

export function buildFallbackPlayStoreURL(source: string | null): string {
  const utmSource = source ? encodeURIComponent(source) : 'unknown'
  return `${FALLBACK_PLAY_STORE_URL}?utm_source=${utmSource}&utm_campaign=summer_2025`
}
